import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Dialog } from "primereact/dialog";
import Langs, { Localize } from "../../../app/lang/langs";
import { InputField } from "../../inputs/InputField";
import { DynamicButton } from "../../buttons/DynamicButton";
import { Row } from "primereact/row";
import { Col } from "react-bootstrap";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";
import NewsReadCheckbox from "./NewsReadCheckbox";
import NewsAttachmentsView from "./NewsAttachmentsView";
import NewsAudienceChips from "./NewsAudienceChips";
import NewsViewersDialog from "./NewsViewersDialog";
import { checkPermission, getNewsCategoryColor } from "../../../app/utils/helpers";
import { config, NEWS_CATEGORY_LANG_KEYS } from "../../../app/utils/config";

// Outlook-style importance marker: a red "!" in a solid circle rather than a labeled tag.
function NewsMandatoryMark({ size = 18 }) {
    return (
        <Box
            title={Localize("NEWS_MANDATORY_TAG")}
            sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                backgroundColor: "#d32f2f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            <PriorityHighRoundedIcon sx={{ fontSize: size * 0.75, color: "#fff" }} />
        </Box>
    )
}


export default function NewsModal(props) {

    const isMobile = useSelector((state) => state.settings.isMobile)
    const newsList = useSelector((state) => state.dashboard.news)
    const [updatedItem, setUpdatedItem] = useState(props.selectedNews)
    const [viewersOpen, setViewersOpen] = useState(false)

    const mobile = isMobile === 2
    // The audience and the read receipts are publisher information: receipts name
    // individuals (the route checks the same scope), and a reader has no use for who
    // else the post was sent to.
    const canManageNews = checkPermission(config.scopes.news, config.permissionTypes.edit)

    // `props.selectedNews` is a snapshot taken when the row was clicked, so read
    // the acknowledgement state from the store to hide the hint once confirmed.
    const storedNews = newsList?.find(news => news.id === props.selectedNews?.id)
    const isAcknowledged = Boolean(storedNews?.seen)
    // Same reason: the store's copy is the one an upload or a deletion updates, the
    // snapshot is not. Falls back to the snapshot for a news not in the list.
    const attachments = (storedNews || props.selectedNews)?.attachments || []
    const category = (storedNews || props.selectedNews)?.category || ""
    const isMandatory = Number((storedNews || props.selectedNews)?.mandatory) === 1

    const handleFieldChange = (value, field) => {
        setUpdatedItem(prev => ({
            ...prev,
            [field.key]: value
        }))
    }

    // Viewing a news item no longer marks it as read — the collab has to tick
    // the acknowledgement checkbox below explicitly.
    useEffect(() => {
        setUpdatedItem({
            ...props.selectedNews
        })
    }, [props.selectedNews])

    const visibleFields = [
        { key: "title", type: "string", disabled: true },
        { key: "message", type: "text", disabled: true, auto: true },
        { key: "created_by", type: "string", disabled: true },
        { key: "created_at", type: "date:time", disabled: true },
    ]

    const handleSave = async () => {

    }

    const footer = () => {
        if (JSON.stringify(props.selectedNews) != JSON.stringify(updatedItem))
            return <Row className="d-flex justify-content-center" >
                <Col lg={2} xs={mobile ? 8 : 2}>
                    <DynamicButton
                        onClick={handleSave}
                        className='me-auto'
                        title={<Langs str="Save" />}
                        active={true}
                        color="green"
                    />
                </Col>
            </Row>
    }

    return (
        <>
            <Dialog
                visible={props.selectedNews}
                className={mobile ? "news-modal-mobile" : ""}
                style={{
                    width: mobile ? "100%" : "50%",
                    maxHeight: mobile ? "85vh" : undefined,
                    borderRadius: mobile ? "16px 16px 0 0" : "18px",
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(255, 144, 21, 0.10), 0 2px 12px rgba(0, 0, 0, 0.08)",
                    border: mobile ? "none" : "1px solid rgba(255, 144, 21, 0.12)",
                    backdropFilter: "blur(16px)",
                    WebkitBackdropFilter: "blur(16px)",
                    background: "rgba(255, 255, 255, 0.88)",
                    ...(mobile ? { margin: 0, bottom: 0, position: "fixed", left: 0, right: 0, top: "auto" } : {}),
                }}
                contentStyle={{
                    padding: mobile ? "16px" : "24px 28px",
                    background: "transparent",
                    overflow: mobile ? "auto" : undefined,
                }}
                headerStyle={{
                    padding: mobile ? "16px 16px 10px" : "20px 28px 12px",
                    background: "linear-gradient(135deg, rgba(255, 144, 21, 0.06) 0%, rgba(255, 193, 0, 0.04) 100%)",
                    borderBottom: "1px solid rgba(255, 144, 21, 0.10)",
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                    color: "#2d2a26",
                    fontSize: "1.1rem",
                }}
                onHide={() => {
                    props.onHide()
                }}
                header={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {isMandatory && <NewsMandatoryMark size={20} />}
                        <Langs str="Actualité" />
                    </Box>
                }
                draggable={false}
                dismissableMask={false}
                footer={footer}
            >
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: mobile ? "12px" : "16px",
                    animation: mobile
                        ? "newsModalSlideUp 300ms cubic-bezier(0.4, 0, 0.2, 1) both"
                        : "newsViewModalFadeIn 280ms cubic-bezier(0.4, 0, 0.2, 1) both",
                }}>
                    {props.selectedNews && props.selectedNews.id && !isAcknowledged && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                color: "#b06000",
                                // Stays pinned to the top of the content while the
                                // message scrolls, so the instruction never scrolls away.
                                position: "sticky",
                                top: mobile ? "-16px" : "-24px",
                                zIndex: 2,
                                mx: mobile ? "-16px" : "-28px",
                                px: mobile ? "16px" : "28px",
                                py: 1,
                                background: "rgba(255, 250, 242, 0.94)",
                                backdropFilter: "blur(8px)",
                                WebkitBackdropFilter: "blur(8px)",
                                borderBottom: "1px solid rgba(255, 144, 21, 0.16)",
                            }}
                        >
                            <KeyboardDoubleArrowDownIcon sx={{ fontSize: 18 }} />
                            <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>
                                <Langs str="NEWS_SCROLL_DOWN_TO_CONFIRM" />
                            </Typography>
                        </Box>
                    )}
                    {category && (
                        <Box sx={{ display: "flex" }}>
                            <Chip
                                label={Localize(NEWS_CATEGORY_LANG_KEYS[category] || category)}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    color: getNewsCategoryColor(category).color,
                                    backgroundColor: getNewsCategoryColor(category).background,
                                    border: getNewsCategoryColor(category).border
                                        ? `1px solid ${getNewsCategoryColor(category).border}`
                                        : "none",
                                }}
                            />
                        </Box>
                    )}
                    {canManageNews && (
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                    }}>
                        <NewsAudienceChips news={storedNews || props.selectedNews} />
                        {props.selectedNews && props.selectedNews.id && (
                            <Button
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => setViewersOpen(true)}
                                sx={{
                                    textTransform: "none",
                                    fontSize: "0.78rem",
                                    fontWeight: 600,
                                    color: "#B06000",
                                    backgroundColor: "rgba(255, 144, 21, 0.09)",
                                    border: "1px solid rgba(255, 144, 21, 0.24)",
                                    borderRadius: "10px",
                                    px: 1.25,
                                    "&:hover": { backgroundColor: "rgba(255, 144, 21, 0.16)" },
                                }}
                            >
                                {Localize("NEWS_VIEWERS")}
                            </Button>
                        )}
                    </Box>
                    )}
                    {visibleFields.map(field => <InputField field={field}
                        onChange={(e) => handleFieldChange(e, field)}
                        value={updatedItem && updatedItem[field.key]} />
                    )}
                    <NewsAttachmentsView attachments={attachments} />
                    {props.selectedNews && props.selectedNews.id && (
                        <NewsReadCheckbox news={props.selectedNews} />
                    )}
                </div>
            </Dialog>
            {canManageNews && (
                <NewsViewersDialog
                    newsId={props.selectedNews && props.selectedNews.id}
                    title={props.selectedNews && props.selectedNews.title}
                    createdAt={props.selectedNews && props.selectedNews.created_at}
                    open={viewersOpen}
                    onClose={() => setViewersOpen(false)}
                />
            )}
            <style>{`
                @keyframes newsViewModalFadeIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes newsModalSlideUp {
                    from { opacity: 0; transform: translateY(40px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .news-modal-mobile input,
                .news-modal-mobile textarea {
                    font-size: 16px !important;
                }
                .news-modal-mobile .p-dialog-header-icons .p-dialog-header-icon {
                    width: 44px !important;
                    height: 44px !important;
                    min-width: 44px;
                    min-height: 44px;
                }
                .news-modal-mobile + .p-dialog-mask,
                .news-modal-mobile.p-dialog + .p-component-overlay {
                    backdrop-filter: blur(6px);
                    -webkit-backdrop-filter: blur(6px);
                }
            `}</style>
        </>
    )
}
